import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Alert,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Divider,
  Stack,
  Avatar,
  alpha,
  TextField,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  Inventory2 as Inventory2Icon,
  Add as AddIcon,
  Link as LinkIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useState, useMemo, useEffect } from 'react';
import type { StockReviewDecision, StockMatch } from '../../types/stock';
import { StockReviewDecisionType } from '../../types/stock';
import type { StockReviewWithMatches } from '../../types/stock';
import InstructionsPanel from './InstructionsPanel';
import StockList from './StockList';
import {
  CancelWarningDialog,
  MapConfirmationDialog,
} from './ConfirmationDialogs';

interface StockReviewModalProps {
  request: StockReviewWithMatches | null;
  onClose: () => void;
  onSubmit: (decision: StockReviewDecision) => Promise<void>;
  totalReviews?: number;
  currentReviewNumber?: number;
}

const getMatchColor = (score: number) => {
  if (score >= 90) return 'success';
  if (score >= 75) return 'warning';
  return 'default';
};

const StockReviewModal: React.FC<StockReviewModalProps> = ({
  request,
  onClose,
  onSubmit,
  totalReviews = 1,
  currentReviewNumber = 1,
}) => {
  const showProgress = totalReviews > 1;
  const [selectedStock, setSelectedStock] = useState<string | null>(null);
  const [decision, setDecision] = useState<StockReviewDecisionType>(
    StockReviewDecisionType.MapExisting,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [showMapConfirmation, setShowMapConfirmation] = useState(false);

  const matchingStocks: StockMatch[] = request?.matchingStocks ?? [];

  useEffect(() => {
    if (request?.reviewId) {
      setSelectedStock(null);
      setDecision(StockReviewDecisionType.MapExisting);
      setSearchQuery('');
      setError(null);
      setShowCancelWarning(false);
      setShowMapConfirmation(false);
    }
  }, [request?.reviewId]);

  useEffect(() => {
    if (matchingStocks.length > 0 && !selectedStock) {
      const highestMatch = matchingStocks.reduce((prev, current) =>
        (current.similarityScore ?? 0) > (prev.similarityScore ?? 0)
          ? current
          : prev,
      );
      if (highestMatch?.stockId) {
        setSelectedStock(highestMatch.stockId);
      }
    }
  }, [matchingStocks, selectedStock]);

  const filteredStocks = useMemo(() => {
    if (!searchQuery.trim()) return matchingStocks;
    const query = searchQuery.toLowerCase();
    return matchingStocks.filter(
      stock =>
        stock.stockName?.toLowerCase().includes(query) ||
        stock.stockUnit?.toLowerCase().includes(query),
    );
  }, [matchingStocks, searchQuery]);

  if (!request) return null;

  const handleSubmit = async () => {
    if (decision === StockReviewDecisionType.MapExisting && !selectedStock) {
      setError('Please select a stock item to map to');
      return;
    }
    if (decision === StockReviewDecisionType.MapExisting) {
      setShowMapConfirmation(true);
      return;
    }
    await executeSubmit();
  };

  const executeSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await onSubmit({
        ReviewId: request.reviewId,
        Decision: decision,
        SelectedStockId:
          decision === StockReviewDecisionType.MapExisting && selectedStock
            ? selectedStock
            : undefined,
        NewStockName:
          decision === StockReviewDecisionType.CreateNew
            ? request.extractedStockName
            : undefined,
      });
      // Don't call onClose() here! The hook's submitDecision handles navigation
      // If there are more reviews, it advances; if done, it clears state (which closes modal)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to submit stock review';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMap = async () => {
    setShowMapConfirmation(false);
    await executeSubmit();
  };

  const handleDecisionChange = (newDecision: StockReviewDecisionType) => {
    setDecision(newDecision);
    setError(null);
    if (newDecision === StockReviewDecisionType.CreateNew) {
      setSelectedStock(null);
    }
  };

  const handleCancelClick = () => {
    if (!loading) {
      setShowCancelWarning(true);
    }
  };

  const handleConfirmCancel = async () => {
    setShowCancelWarning(false);
    setLoading(true);
    setError(null);
    try {
      await onSubmit({
        ReviewId: request.reviewId,
        Decision: StockReviewDecisionType.CreateNew,
        NewStockName: request.extractedStockName,
      });
      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create stock item';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedStock(null);
      setDecision(StockReviewDecisionType.MapExisting);
      setError(null);
      setSearchQuery('');
      setShowCancelWarning(false);
      onClose();
    }
  };

  const handleSelectStock = (stockId: string) => {
    setSelectedStock(stockId);
    setError(null);
  };

  const selectedStockName =
    matchingStocks.find(s => s.stockId === selectedStock)?.stockName ??
    'Selected Stock';

  return (
    <>
      <Dialog
        open={!!request}
        onClose={handleCancelClick}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' },
        }}>
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            p: 2.5,
          }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: alpha('#fff', 0.2), width: 44, height: 44 }}>
              <Inventory2Icon />
            </Avatar>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography variant="h6" fontWeight="bold" fontSize="1.1rem">
                  Stock Review Required
                </Typography>
                {showProgress && (
                  <Chip
                    label={`${currentReviewNumber} of ${totalReviews}`}
                    size="small"
                    sx={{
                      bgcolor: alpha('#fff', 0.2),
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                    }}
                  />
                )}
              </Stack>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {showProgress
                  ? `Review ${currentReviewNumber} of ${totalReviews} stock items`
                  : 'Match or create a new stock item entry'}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex' }}>
          <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
            <InstructionsPanel />

            {/* Main Content */}
            <Box sx={{ flex: 1, p: 2.5, overflow: 'auto' }}>
              {/* Extracted Stock Info */}
              <Card
                variant="outlined"
                sx={{
                  mb: 2.5,
                  bgcolor: alpha('#1976d2', 0.04),
                  borderColor: alpha('#1976d2', 0.2),
                }}>
                <CardContent sx={{ py: 1.5, px: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    textTransform="uppercase"
                    fontWeight="medium"
                    fontSize="0.65rem">
                    Extracted Stock Item Name
                  </Typography>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                    sx={{ mt: 0.25 }}>
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      fontWeight="bold"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}>
                      {request.extractedStockName}
                    </Typography>
                    {request.stockUnit && (
                      <Chip
                        label={`Unit: ${request.stockUnit}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: '0.7rem',
                          height: 24,
                          borderColor: alpha('#1976d2', 0.3),
                          color: 'text.secondary',
                        }}
                      />
                    )}
                  </Stack>
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    {matchingStocks.length > 0 &&
                      matchingStocks[0].vendorName && (
                        <Typography variant="caption" color="text.secondary">
                          Vendor:{' '}
                          <strong>{matchingStocks[0].vendorName}</strong>
                        </Typography>
                      )}
                    {matchingStocks.length > 0 &&
                      matchingStocks[0].companyName && (
                        <Typography variant="caption" color="text.secondary">
                          Company:{' '}
                          <strong>{matchingStocks[0].companyName}</strong>
                        </Typography>
                      )}
                    {request.fileName && (
                      <Typography variant="caption" color="text.secondary">
                        File: <strong>{request.fileName}</strong>
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 2, borderRadius: 2, py: 0.5 }}>
                  {error}
                </Alert>
              )}

              <RadioGroup
                value={decision}
                onChange={e =>
                  handleDecisionChange(
                    e.target.value as StockReviewDecisionType,
                  )
                }>
                {/* Map to Existing Option */}
                <Card
                  variant="outlined"
                  sx={{
                    mb: 1.5,
                    borderColor:
                      decision === StockReviewDecisionType.MapExisting
                        ? 'primary.main'
                        : 'divider',
                    borderWidth:
                      decision === StockReviewDecisionType.MapExisting ? 2 : 1,
                    transition: 'all 0.2s ease',
                  }}>
                  <CardActionArea
                    onClick={() =>
                      handleDecisionChange(StockReviewDecisionType.MapExisting)
                    }
                    sx={{ p: 0 }}>
                    <CardContent sx={{ py: 1.5, px: 2 }}>
                      <FormControlLabel
                        value={StockReviewDecisionType.MapExisting}
                        control={<Radio size="small" />}
                        label={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}>
                            <LinkIcon color="primary" fontSize="small" />
                            <Typography fontWeight="medium" fontSize="0.9rem">
                              Map to existing stock item
                            </Typography>
                          </Stack>
                        }
                        sx={{ m: 0 }}
                      />
                    </CardContent>
                  </CardActionArea>

                  {decision === StockReviewDecisionType.MapExisting &&
                    matchingStocks.length > 0 && (
                      <>
                        <Divider />
                        <Box sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                          <Autocomplete
                            options={matchingStocks}
                            getOptionLabel={(option: StockMatch) =>
                              option.stockName || ''
                            }
                            filterOptions={(options, { inputValue }) => {
                              const query = inputValue.toLowerCase();
                              return options.filter(
                                option =>
                                  option.stockName
                                    ?.toLowerCase()
                                    .includes(query) ||
                                  option.stockUnit
                                    ?.toLowerCase()
                                    .includes(query) ||
                                  option.vendorName
                                    ?.toLowerCase()
                                    .includes(query) ||
                                  option.companyName
                                    ?.toLowerCase()
                                    .includes(query),
                              );
                            }}
                            inputValue={searchQuery}
                            onInputChange={(_, value) => setSearchQuery(value)}
                            onChange={(_, value) => {
                              if (value) {
                                setSelectedStock(value.stockId);
                                setSearchQuery(value.stockName || '');
                                setError(null);
                              }
                            }}
                            autoHighlight
                            autoSelect
                            selectOnFocus
                            handleHomeEndKeys
                            openOnFocus
                            blurOnSelect
                            clearOnBlur={false}
                            renderOption={(props, option: StockMatch) => (
                              <Box
                                component="li"
                                {...props}
                                key={option.stockId}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  width="100%">
                                  <Box>
                                    <Typography
                                      fontSize="0.875rem"
                                      fontWeight="medium">
                                      {option.stockName}
                                    </Typography>
                                    {(option.stockUnit ||
                                      option.vendorName ||
                                      option.companyName) && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary">
                                        {[
                                          option.stockUnit,
                                          option.vendorName,
                                          option.companyName,
                                        ]
                                          .filter(Boolean)
                                          .join(' • ')}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Chip
                                    label={`${option.similarityScore}%`}
                                    size="small"
                                    color={getMatchColor(
                                      option.similarityScore,
                                    )}
                                    sx={{
                                      fontWeight: 'bold',
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                </Stack>
                              </Box>
                            )}
                            renderInput={params => (
                              <TextField
                                {...params}
                                placeholder="Search stock items... (Tab to select)"
                                size="small"
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon
                                        fontSize="small"
                                        sx={{ color: 'text.secondary' }}
                                      />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  mb: 1.5,
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                  },
                                }}
                              />
                            )}
                            ListboxProps={{
                              sx: {
                                maxHeight: 200,
                                '& .MuiAutocomplete-option': { py: 1 },
                              },
                            }}
                          />

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="medium"
                            sx={{ mb: 1, display: 'block' }}
                            fontSize="0.65rem">
                            {filteredStocks.length} MATCHING STOCK ITEM
                            {filteredStocks.length !== 1 ? 'S' : ''}
                          </Typography>

                          <StockList
                            stocks={filteredStocks}
                            selectedStock={selectedStock}
                            onSelectStock={handleSelectStock}
                          />
                        </Box>
                      </>
                    )}
                </Card>

                {/* Create New Option */}
                <Card
                  variant="outlined"
                  sx={{
                    borderColor:
                      decision === StockReviewDecisionType.CreateNew
                        ? 'primary.main'
                        : 'divider',
                    borderWidth:
                      decision === StockReviewDecisionType.CreateNew ? 2 : 1,
                    transition: 'all 0.2s ease',
                  }}>
                  <CardActionArea
                    onClick={() =>
                      handleDecisionChange(StockReviewDecisionType.CreateNew)
                    }
                    sx={{ p: 0 }}>
                    <CardContent sx={{ py: 1.5, px: 2 }}>
                      <FormControlLabel
                        value={StockReviewDecisionType.CreateNew}
                        control={<Radio size="small" />}
                        label={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}>
                            <AddIcon color="primary" fontSize="small" />
                            <Typography fontWeight="medium" fontSize="0.9rem">
                              Create new stock item
                            </Typography>
                          </Stack>
                        }
                        sx={{ m: 0 }}
                      />
                      {decision === StockReviewDecisionType.CreateNew && (
                        <Box sx={{ ml: 4, mt: 0.5 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontSize="0.8rem"
                            sx={{
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                            }}>
                            New stock item:{' '}
                            <Typography
                              component="span"
                              fontWeight="medium"
                              color="text.primary">
                              "{request.extractedStockName}"
                            </Typography>
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </RadioGroup>
            </Box>
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: 'grey.50' }}>
          <Button
            onClick={handleCancelClick}
            disabled={loading}
            variant="outlined"
            size="small"
            color="warning"
            sx={{ borderRadius: 2, px: 2.5 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="small"
            disabled={
              loading ||
              (decision === StockReviewDecisionType.MapExisting &&
                !selectedStock)
            }
            sx={{ borderRadius: 2, px: 2.5, minWidth: 100 }}>
            {loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <CancelWarningDialog
        open={showCancelWarning}
        onClose={() => setShowCancelWarning(false)}
        onConfirm={handleConfirmCancel}
        loading={loading}
        stockName={request.extractedStockName}
      />

      <MapConfirmationDialog
        open={showMapConfirmation}
        onClose={() => setShowMapConfirmation(false)}
        onConfirm={handleConfirmMap}
        loading={loading}
        extractedStockName={request.extractedStockName}
        selectedStockName={selectedStockName}
      />
    </>
  );
};

export default StockReviewModal;
